import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// One fetch of /me/permissions per logged-in user, shared by every component.
let cache = { userId: null, promise: null, data: null };

function load(userId) {
  if (cache.userId !== userId || !cache.promise) {
    cache = { userId, data: null, promise: api.get("/me/permissions").then(({ data }) => { cache.data = data; return data; }) };
  }
  return cache.promise;
}

export function resetPermissionsCache() {
  cache = { userId: null, promise: null, data: null };
}

/**
 * can(module, action) / canView(module) from the Permission Matrix.
 * `module` may be a string, an array (any of them), or null/undefined (always allowed).
 * ORG_ADMIN can do everything. While loading, only entries without a module are allowed.
 */
export function usePermissions() {
  const { user } = useAuth();
  const [data, setData] = useState(cache.userId === user?.id ? cache.data : null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    load(user.id).then((d) => alive && setData(d)).catch(() => alive && setData({ permissions: {}, failed: true }));
    return () => { alive = false; };
  }, [user?.id]);

  const isAdmin = user?.role === "ORG_ADMIN";
  const can = (module, action = "VIEW") => {
    if (!module) return true;
    if (isAdmin) return true;
    if (!data) return false;
    const list = Array.isArray(module) ? module : [module];
    return list.some((m) => (data.permissions?.[m] || []).includes(action));
  };
  return { loading: !data && !isAdmin, can, canView: (m) => can(m, "VIEW") };
}
